/** @jsx jsx */
import React from 'react';
import PropTypes from 'prop-types';
import { jsx } from '@emotion/core';
import get from 'lodash/get';
import findIndex from 'lodash/findIndex';
import { Icon } from '../../Icon';
import { SafePureComponent } from '../../../../utils/SafePureComponent';
import {
  buildImagePath,
  parseUrlConfigParams,
} from '../../../../utils/cloudinary';
import {
  CLOUDINARY_BASE_VALUES,
  DEFAULT_COUNTRY,
} from '../../../../utils/constants';
import {
  errorCss,
  selectIconCss,
  selectInputCss,
} from '../baseStyles/index.styles';
import {
  selectContainer,
  phoneNumberContainer,
  flagArrowContainer,
  inputCss,
  iconCss,
  labelCss,
  numberPlaceholder,
  imageContainer,
  shrinkPlaceholder,
  imageCss,
  inputDisabled,
  phoneNumberHolder,
  inputLabelHolder,
  selectArrowIconCss,
} from './index.styles';

const flagSizeConfig = parseUrlConfigParams({
  ...CLOUDINARY_BASE_VALUES,
  width: 32,
}).config;

const renderHTML = rawHTML =>
  React.createElement('div', {
    dangerouslySetInnerHTML: { __html: rawHTML },
  });

class PhoneNumberField extends SafePureComponent {
  phoneNumberInputRef = React.createRef();
  selectRef = React.createRef();

  state = {
    selectedIndex: this.findCountryIndex(this.props.defaultValue),
    focus: false,
    shrink: false,
    countryData: this.initialCountryData(),
    baseUrl: '',
  };

  componentDidMount() {
    this.props.onInit({ countryData: this.state.countryData });
    if (typeof window !== 'undefined') {
      this.setState({ baseUrl: window.location.origin });
    }

    const { inputProps } = this.props;
    if (inputProps.value) {
      this.setState({ shrink: true });
    }
  }

  findCountryIndex(value) {
    // Since sitecore's default value is 'N/A' we need to validate the lenght of the ISOcode2
    const countryValue = value.length === 2 ? value : DEFAULT_COUNTRY;
    const countryIndex = findIndex(
      this.props.options,
      option => get(option, 'fields.CountryISOCode2.value') === countryValue,
    );

    return countryIndex;
  }

  getCountryValue(value, index) {
    return get(this.props, `options[${index}].fields[${value}].value`, '');
  }

  initialCountryData() {
    const initialIndex = this.findCountryIndex(this.props.defaultValue);
    const CountryISOCode2 = this.getCountryValue(
      'CountryISOCode2',
      initialIndex,
    );
    const CountryISOCode3 = this.getCountryValue(
      'CountryISOCode3',
      initialIndex,
    );
    const DialingCode = this.getCountryValue('DialingCode', initialIndex);

    return { CountryISOCode2, CountryISOCode3, DialingCode };
  }

  onChangeSelect = event => {
    const { value: selectedIndex } = event.currentTarget;
    const CountryISOCode2 = this.getCountryValue(
      'CountryISOCode2',
      selectedIndex,
    );
    const CountryISOCode3 = this.getCountryValue(
      'CountryISOCode3',
      selectedIndex,
    );
    const DialingCode = this.getCountryValue('DialingCode', selectedIndex);

    if (typeof this.props.onChangeProp === 'function') {
      this.props.onChangeProp(event, {
        CountryISOCode2,
        CountryISOCode3,
        DialingCode,
      });
    }

    this.phoneNumberInputRef.current.focus();

    this.setState({
      selectedIndex,
      shrink: true,
      countryData: { CountryISOCode2, CountryISOCode3, DialingCode },
    });
  };

  onFocusField = () => {
    this.setState({ focus: true, shrink: true });
    const { inputOnFocus } = this.props;
    if (inputOnFocus) {
      inputOnFocus();
    }
  };

  onBlurField = event => {
    const { value } = event.target;
    const shouldShrink = value.length > 0;
    this.setState({ focus: false, shrink: shouldShrink });
  };

  getPhoneNumberOptions(options) {
    return options.map(({ fields }, index) => {
      const country = get(fields, 'CountryName.value', '');
      return (
        <option
          key={index}
          value={index}
          selected={this.state.selectedIndex === index}
        >
          {country}
        </option>
      );
    });
  }

  getflagImageUrl() {
    const relativeImgUrl = get(
      this.props.options[this.state.selectedIndex],
      'fields.Flag.value',
      '',
    );

    return buildImagePath(flagSizeConfig, relativeImgUrl);
    // return '/' + relativeImgUrl;
  }

  safeRender() {
    const {
      options,
      error,
      errorMessage,
      id,
      // placeholder,
      onChange,
      customCss,
      maxlength,
      className,
      innerCss = {},
      inputProps = {},
      selectProps = {},
    } = this.props;

    let { placeholder } = this.props;
    placeholder = placeholder.split('*').join(' <span>*</span>');
    placeholder = renderHTML(placeholder);

    const { shrink, focus, selectedIndex } = this.state;

    const isDisabled = get(inputProps, 'disabled', false);

    return (
      <div css={[customCss, isDisabled && inputDisabled]} className={className}>
        <div
          data-error={error}
          data-shrink={shrink}
          data-focus={focus}
          css={[phoneNumberContainer, innerCss.container]}
        >
          <div css={[phoneNumberHolder]}>
            <label
              data-shrink={shrink}
              data-focus={focus}
              data-error={error}
              htmlFor={`id-${placeholder}`}
              css={[shrinkPlaceholder, innerCss.placeholder]}
            >
              {placeholder}
            </label>

            {options && (
              <React.Fragment>
                <div css={selectContainer}>
                  <span css={flagArrowContainer}>
                    <span css={imageContainer}>
                      <img
                        css={imageCss}
                        src={this.getflagImageUrl()}
                        alt={get(
                          options[selectedIndex],
                          'fields.CountryName.value',
                          '',
                        )}
                      />
                    </span>
                    <Icon
                      name="chevron-down"
                      customCss={[selectIconCss, selectArrowIconCss, iconCss]}
                    />
                  </span>
                  <select
                    css={[selectInputCss, { cursor: 'pointer' }]}
                    name={placeholder}
                    onChange={this.onChangeSelect}
                    onFocus={this.onFocusField}
                    onBlur={this.onBlurField}
                    ref={this.selectRef}
                    {...selectProps}
                  >
                    {this.getPhoneNumberOptions(options)}
                  </select>
                </div>
                <span css={[numberPlaceholder, innerCss.dialingCode]}>
                  {get(options[selectedIndex], 'fields.DialingCode.value', '')}
                </span>
                <div css={[inputLabelHolder]}>
                  <label
                    data-shrink={shrink}
                    htmlFor={`id-${placeholder}`}
                    css={[labelCss, innerCss.inputValue]}
                  >
                    {placeholder}
                  </label>
                  <input
                    css={[inputCss, innerCss.input]}
                    type="text"
                    id={id}
                    onChange={onChange}
                    maxLength={maxlength}
                    onFocus={this.onFocusField}
                    onBlur={this.onBlurField}
                    ref={this.phoneNumberInputRef}
                    {...inputProps}
                  />
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
        <span id={`${id}-helper-text`} css={errorCss}>
          {error && errorMessage}
        </span>
      </div>
    );
  }
}

PhoneNumberField.displayName = 'PhoneNumberField';

PhoneNumberField.defaultProps = {
  placeholder: '',
  error: false,
  errorMessage: '',
  onChange: () => {},
  onChangeProp: () => {},
  options: [],
  defaultValue: DEFAULT_COUNTRY,
  maxlength: '20',
  className: '',
  inputProps: {},
  selectProps: {},
  onInit: () => {},
};

PhoneNumberField.propTypes = {
  placeholder: PropTypes.string.isRequired,
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  maxlength: PropTypes.string,
  className: PropTypes.string,
  onChangeProp: PropTypes.func,
  onChange: PropTypes.func,
  options: PropTypes.array.isRequired,
  inputProps: PropTypes.any,
  selectProps: PropTypes.any,
  onInit: PropTypes.func,
};

export default PhoneNumberField;
